import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import ModalCustom from '../../UI/modal';
import ThInputText from '../../forms/th-input-text';
import Button from '../../UI/button';
import { IPlacesModel, useAddActivity } from './use-add-activity';
import { ISearchBiasModel } from '../../../Models/search-bias.model';
import { useTrip } from '../../contexts/trip-context';
import aiService from '../../../services/ai.service';
import imageUploadService from '../../../services/image-upload.service';
import { v4 as uuidv4 } from 'uuid';
import {
  placesAutocomplete,
  getPlaceDetails
} from '../../../services/map.service';
import placesService from '../../../services/places.service';

type Props = {
  show: boolean;
  handleClose: () => void;
  place?: IPlacesModel;
  bias?: ISearchBiasModel;
  parentFolderId?: string;
  tripId?: string;
};

const AddActivityFromImageModal = ({
  show,
  handleClose,
  parentFolderId: propParentFolderId,
  tripId: propTripId,
  place,
  bias
}: Props) => {
  const { trip, activeFolderIds } = useTrip();

  // Use context mode if props are not provided
  const tripId = propTripId || trip?.id || '';
  const parentFolderId =
    propParentFolderId ||
    (activeFolderIds && activeFolderIds.length > 0 ? activeFolderIds[0] : '') ||
    '';

  const {
    activity,
    validated,
    isSubmitting,
    setPlace,
    handleInputChange,
    handleSubmit
  } = useAddActivity(tripId, parentFolderId, handleClose);

  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (place) {
      setPlace({ id: place.placesId, ...place });
    }
  }, [place, setPlace]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result as string;
      setPreview(dataUrl);
      setUploading(true);

      try {
        const res = await aiService.processImage(dataUrl);
        // res is expected to be { status: 'success', data: '<model text>' }
        const modelText = res?.data;
        let parsed: any = null;
        if (typeof modelText === 'string') {
          try {
            parsed = JSON.parse(modelText);
          } catch (err) {
            // Try to extract JSON substring
            const match = modelText.match(/\{[\s\S]*\}/);
            if (match) {
              try {
                parsed = JSON.parse(match[0]);
              } catch (e) {
                // ignore
              }
            }
          }
        } else if (typeof modelText === 'object') {
          parsed = modelText;
        }

        if (!parsed) {
          setError('AI result could not be parsed into JSON');
          setUploading(false);
          return;
        }

        const { suggestedTitle, location, description } = parsed;

        if (suggestedTitle) {
          handleInputChange('title', suggestedTitle);
        }
        if (description) {
          handleInputChange('description', description);
        }

        // Try to find a place via server-side autocomplete
        try {
          const sessionToken = uuidv4();
          const query = suggestedTitle + (location ? ' ' + location : '');
          const autoRes = await placesAutocomplete(query, sessionToken, bias);
          if (
            autoRes?.status === 'success' &&
            autoRes.data.suggestions.length > 0
          ) {
            const top = autoRes.data.suggestions[0];
            const placeId =
              top.placePrediction?.placeId || top.placeId || top.place_id;
            const details = await placesService.getPlaceDetails(
              placeId || '',
              sessionToken
            );
            if (details?.status === 'success' && details) {
              const fetched = details;
              // Build minimal place object expected by setPlace
              const placeObj: any = {
                id: placeId,
                placesId: placeId,
                name: fetched.title || fetched.name || suggestedTitle,
                latitude: fetched.latitude || undefined,
                longitude: fetched.longitude || undefined,
                types: fetched.types || [],
                websiteUrl: fetched.websiteUrl || undefined,
                description: fetched.description || undefined,
                addressComponents: fetched.addressComponents || []
              };
              setPlace(placeObj);
            }
          }
        } catch (err) {
          console.warn('Error finding place from AI suggestion:', err);
        }

        // Upload image to S3 first
        try {
          const s3Key = await imageUploadService.uploadBase64Image(dataUrl);
          // Store the S3 key instead of the base64 data URL
          handleInputChange('imageUrl', s3Key);
        } catch (err) {
          console.error('Error uploading image to S3:', err);
          // Fallback to data URL if upload fails
          handleInputChange('imageUrl', dataUrl);
        }

        // Remove auto-submit - let user review and click the submit button
      } catch (err: any) {
        console.error('Error processing image with AI:', err);
        setError(err?.message || 'Error processing image');
      } finally {
        setUploading(false);
      }
    };

    reader.readAsDataURL(file);
  };

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (validated) {
      handleSubmit(e);
    }
  };

  return (
    <ModalCustom
      show={show}
      onHide={handleClose}
      title='Add Activity from Image'
    >
      <Form onSubmit={handleFormSubmit}>
        <Form.Group className='mb-3'>
          <Form.Label>Upload an Image</Form.Label>
          <Form.Control
            type='file'
            accept='image/*'
            onChange={handleFileChange}
          />
        </Form.Group>

        {preview && (
          <div style={{ textAlign: 'center', marginBottom: '12px' }}>
            <img
              src={preview}
              alt='preview'
              style={{ maxWidth: '100%', maxHeight: 240 }}
            />
          </div>
        )}

        {error && <div className='text-danger mb-2'>{error}</div>}
        {/* <ActivityAutocomplete
          label='Title'
          placeholder='Enter title'
          setPlace={(p) => {
            setPlace(p);
            setSelectedPlace(p as IPlacesModel);
          }}
          bias={bias}
          place={selectedPlace}
        /> */}
        <ThInputText
          label='Title'
          placeholder='Enter title'
          value={activity.title}
        />
        <ThInputText
          label='Website Link'
          placeholder='Enter here'
          value={activity.websiteUrl ?? ''}
          onChange={(value) => handleInputChange('websiteUrl', value)}
        />

        <Button
          className='mx-auto'
          style={{ marginTop: '24px' }}
          px={50}
          variant='primary'
          type='submit'
          disabled={!validated || isSubmitting || uploading}
        >
          {isSubmitting || uploading ? 'Adding...' : 'Add Activity'}
        </Button>
      </Form>
    </ModalCustom>
  );
};

export default AddActivityFromImageModal;
